import { useState } from 'react';

import { useCreateMailingList, useMailingLists, type MailingListCreateInput } from '../../api/mailing-lists';
import { Badge, Button, EmptyState, ErrorState, Spinner } from '../../components/ui';
import { TableWrap } from '../../components/TableWrap';
import { MailingListForm } from './MailingListForm';
import {
  channelLabel,
  channelOf,
  describeSelection,
  formatTemplatePin,
  formatWhen,
  isArchived,
  selectionModeLabel,
} from './lists-model';

/**
 * The campaign INDEX of the mailing LISTS surface (#34.1, T.Z132): every list,
 * live first / archived last, with its two head numbers, and the create fold
 * above it. A row click opens the list view (`?list=<id>` — the caller owns the
 * URL); create answers #34.2's fresh list, whose FIRST resolution already ran
 * inside the request, so opening it shows recipients, not an empty table.
 */

export function MailingListsSection({ onOpen }: { onOpen: (listId: number) => void }) {
  const [creating, setCreating] = useState(false);
  const lists = useMailingLists();
  const create = useCreateMailingList();

  const submitCreate = (input: MailingListCreateInput) =>
    create.mutate(input, {
      onSuccess: (answer) => {
        setCreating(false);
        onOpen(answer.data.list.id);
      },
    });

  return (
    <div className="mailings-lists" data-testid="mailing-lists">
      <section className="card">
        <div className="mailings-filters">
          <h2>Списки рассылок</h2>
          <p className="mailings-lists-hint">
            Список = кто (отбор и его снятый «слепок» получателей), какое письмо (шаблон и его версия) и от чьего имени.
            Отправка писем по списку — отдельный шаг.
          </p>
          <Button
            variant="primary"
            onClick={() => setCreating((prev) => !prev)}
            data-testid="mailing-lists-create-toggle"
          >
            {creating ? 'Свернуть' : 'Создать список'}
          </Button>
        </div>

        {creating ? (
          <div className="mailings-list-form-wrap" data-testid="mailing-list-create">
            <MailingListForm
              mode="create"
              pending={create.isPending}
              error={create.error?.message ?? null}
              submitLabel="Создать и посчитать"
              onCancel={() => setCreating(false)}
              onSubmit={submitCreate}
            />
          </div>
        ) : null}
      </section>

      <section className="card card--flush" data-testid="mailing-lists-table-card">
        {lists.isPending ? (
          <Spinner label="Загружаем списки" />
        ) : lists.isError ? (
          <ErrorState message={String(lists.error)} onRetry={() => void lists.refetch()} />
        ) : (lists.data?.lists.length ?? 0) === 0 ? (
          <EmptyState
            title="Пока нет ни одного списка"
            hint="Создайте список — выберите получателей, шаблон и адрес отправителя."
          />
        ) : (
          <TableWrap>
            <table className="data mailings-table" data-testid="mailing-lists-table">
              <thead>
                <tr>
                  <th>Название</th>
                  <th>Тип</th>
                  <th>От кого</th>
                  <th>Шаблон</th>
                  <th>Отбор</th>
                  <th>Получателей</th>
                  <th>Состав обновлён</th>
                  <th>Статус</th>
                </tr>
              </thead>
              <tbody>
                {(lists.data?.lists ?? []).map((list) => (
                  <tr
                    key={list.id}
                    className="mailings-row"
                    onClick={() => onOpen(list.id)}
                    data-testid="mailing-list-row"
                  >
                    <td className="cell-primary">
                      {list.name}{' '}
                      <Badge tone={channelOf(list) === 'SMS' ? 'brand' : 'neutral'}>{channelLabel(channelOf(list))}</Badge>
                    </td>
                    <td className="cell-muted">{list.mailingType}</td>
                    <td className="cell-muted">{list.fromEmail}</td>
                    <td>{formatTemplatePin(list.template)}</td>
                    <td className="mailings-program" title={describeSelection(list.selectionMode, list.selectionPayload)}>
                      {selectionModeLabel(list.selectionMode)} · {describeSelection(list.selectionMode, list.selectionPayload)}
                    </td>
                    <td className="cell-compact" data-testid="mailing-list-row-count">
                      {list.counts.total}
                      <span className="cell-muted"> ({list.counts.renderReady} готовы)</span>
                    </td>
                    <td className="cell-compact cell-muted">{formatWhen(list.lastResolvedAt)}</td>
                    <td className="cell-compact">
                      {isArchived(list) ? <Badge tone="neutral">В архиве</Badge> : <Badge tone="ok">Активен</Badge>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        )}
      </section>
    </div>
  );
}
